import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import { toggleLike, getLikeStatus } from '../services/likeService';
import { useLanguage } from '../contexts/LanguageContext';
import LikedByModal from './LikedByModal';

/**
 * Reusable like button component for events and songs.
 *
 * @param {string} contentType - 'event' or 'song'
 * @param {number} contentId - ID of the content
 * @param {boolean} [initialLiked] - Initial liked state (skip fetch if provided alongside initialCount)
 * @param {number} [initialCount] - Initial like count (skip fetch if provided alongside initialLiked)
 * @param {function} [onToggle] - Optional callback after toggling: (liked, likeCount) => void
 * @param {string} [size] - FontAwesome size prop (e.g. 'sm', 'lg')
 * @param {boolean} [showCount=true] - Whether to show the count number
 * @param {string} [theme='default'] - 'default' or 'light' (for dark backgrounds)
 * @param {boolean} [showLikedByText=false] - Show "Liked by X, Y and N others" text (for events)
 */
const LikeButton = ({
  contentType,
  contentId,
  initialLiked,
  initialCount,
  onToggle,
  size,
  showCount = true,
  theme = 'default',
  showLikedByText = false,
}) => {
  const { t } = useLanguage();
  const hasInitial = initialLiked !== undefined && initialCount !== undefined;
  const [liked, setLiked] = useState(hasInitial ? initialLiked : false);
  const [likeCount, setLikeCount] = useState(hasInitial ? initialCount : 0);
  const [likedBy, setLikedBy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [likedByFetched, setLikedByFetched] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Fetch like status on mount if no initial values provided
  useEffect(() => {
    if (hasInitial || !contentId) return;
    let cancelled = false;
    getLikeStatus(contentType, contentId)
      .then((data) => {
        if (!cancelled) {
          setLiked(data.liked);
          setLikeCount(data.like_count);
          if (data.liked_by) {
            setLikedBy(data.liked_by);
            setLikedByFetched(true);
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contentType, contentId, hasInitial]);

  // Sync from parent if initial values change
  useEffect(() => {
    if (hasInitial) {
      setLiked(initialLiked);
      setLikeCount(initialCount);
    }
  }, [initialLiked, initialCount, hasInitial]);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (loading || !contentId) return;
    setLoading(true);

    // Optimistic update
    const prevLiked = liked;
    const prevCount = likeCount;
    const newLiked = !liked;
    const newCount = newLiked ? likeCount + 1 : likeCount - 1;
    setLiked(newLiked);
    setLikeCount(newCount);

    try {
      const result = await toggleLike(contentType, contentId);
      setLiked(result.liked);
      setLikeCount(result.like_count);
      if (result.liked_by) {
        setLikedBy(result.liked_by);
        setLikedByFetched(true);
      }
      if (onToggle) onToggle(result.liked, result.like_count);
    } catch {
      setLiked(prevLiked);
      setLikeCount(prevCount);
    } finally {
      setLoading(false);
    }
  };

  const handleTextClick = (e) => {
    e.stopPropagation();
    if (likeCount === 0) return;
    // Lazy-fetch likedBy if not loaded yet
    if (!likedByFetched && contentId) {
      getLikeStatus(contentType, contentId)
        .then((data) => {
          if (data.liked_by) setLikedBy(data.liked_by);
          setLikedByFetched(true);
        })
        .catch(() => {});
    }
    setShowModal(true);
  };

  const getLikedByText = () => {
    const label = t('events.likes.likedBy') || 'Liked by';
    const andWord = t('events.likes.and') || 'and';

    if (likedByFetched && likedBy.length > 0) {
      const names = likedBy.map((u) => u.display_name);
      if (names.length === 1) {
        return `${label} ${names[0]}`;
      }
      if (names.length === 2) {
        return `${label} ${names[0]} ${andWord} ${names[1]}`;
      }
      const othersCount = names.length - 2;
      const othersWord = othersCount === 1
        ? (t('events.likes.other') || 'other')
        : (t('events.likes.others') || 'others');
      return `${label} ${names[0]}, ${names[1]} ${andWord} ${othersCount} ${othersWord}`;
    }

    // Fallback when likedBy hasn't been fetched yet — use count
    if (likeCount === 1) {
      return `${label} 1`;
    }
    return `${label} ${likeCount}`;
  };

  const textColor = theme === 'light' ? '#ffffff' : '#6c757d';

  return (
    <>
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        <button
          className="btn btn-sm p-0 border-0 d-inline-flex align-items-center gap-1"
          onClick={handleClick}
          disabled={loading}
          style={{
            background: 'none',
            color: liked ? '#e8a020' : textColor,
            cursor: loading ? 'default' : 'pointer',
            transition: 'color 0.2s',
          }}
          title={liked ? (t('events.likes.unlike') || 'Unlike') : (t('events.likes.like') || 'Like')}
        >
          <FontAwesomeIcon
            icon={liked ? faHeartSolid : faHeartRegular}
            size={size}
            style={{ transition: 'transform 0.2s', transform: liked ? 'scale(1.15)' : 'scale(1)' }}
          />
        </button>

        {showLikedByText && likeCount > 0 && (
          <span
            onClick={handleTextClick}
            style={{
              fontSize: '0.8em',
              marginLeft: '6px',
              color: textColor,
              cursor: 'pointer',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            {getLikedByText()}
          </span>
        )}

        {!showLikedByText && showCount && likeCount > 0 && (
          <span
            style={{
              fontSize: '0.85em',
              marginLeft: '3px',
              color: textColor,
            }}
          >
            {likeCount}
          </span>
        )}
      </div>

      {showModal && (
        <LikedByModal
          likedBy={likedBy}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default LikeButton;
